'use client'

// A story on the Create feed (flag extend_clips): the video it started from and the clips that continue it, shown as
// one card. Play all runs them in order; each clip can be tried again or discarded (taking the clips after it, which
// continue from it); Continue adds the next clip; Download and Publish join the clips into one video first, on the
// video machines, and hand that video to the feed's own download and publish. Which cards make a story:
// src/lib/clip-chains.ts. New scene (flag story_new_scene) adds a scene instead, joined after the last clip as a cut:
// described in words (the Animate modal with a prompt and no picture) or animated from one of the session's pictures
// (ScenePicker.tsx). Either way it keeps the story's shape.
import { getIdToken } from 'firebase/auth'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  FaDownload,
  FaFilm,
  FaGlobeAmericas,
  FaPlay,
  FaPlus,
  FaRedo,
  FaTrash,
} from 'react-icons/fa'

import Modal from '../Modal'
import useFlag from '../../hooks/useFlag'
import { joinClips } from '../../lib/api/video'
import {
  clipsAfter,
  isClip,
  isScene,
  type ClipItem,
} from '../../lib/clip-chains'
import ScenePicker, { shapeOf, type Picture } from './ScenePicker'

// A scene remembers what it was made from, so its Try again makes it the same way: the picture it was animated from
// (its address only when hosted: a base64 picture is looked up in the feed by id), or the words it was described in
// (Create.jsx adds them when the scene is made).
type SceneOf = {
  parentId?: string
  source?: { id: string; docRef?: string; url?: string; isUrl?: boolean }
  prompt?: string
}
type Clip = ClipItem & {
  url: string
  isUrl?: boolean
  sceneOf?: SceneOf | null
}
/** What the feed's Animate gets for a scene: the picture (none when it is described), the story link the result lands
 * with, and the story's shape, which the scene must keep to join it. */
type SceneRequest = Partial<Picture> & {
  scene: { sceneOf: SceneOf; replacesId?: string; orientation?: Orientation }
}
type Orientation = 'Portrait' | 'Wide' | 'Square'

const ORIENTATION = {
  portrait: 'Portrait',
  landscape: 'Wide',
  square: 'Square',
} as const

type Props = {
  clips: Clip[]
  t: (key: string, options?: Record<string, unknown>) => string
  // firebase's User; typed loosely, as the feed passes it through from the auth context
  user: Parameters<typeof getIdToken>[0] | null
  videoIsLoading: boolean
  onExtend: (item: Clip) => void
  onRedo: (item: Clip) => void
  onDelete: (item: Clip) => unknown
  onDownload: (item: Clip) => void
  onPublish: (item: Clip) => void
  /** The session's pictures, newest first: what a new scene can be animated from. */
  pictures: Picture[]
  onAnimate: (request: SceneRequest) => void
}

type Ask = { kind: 'discard' | 'again'; clip: Clip; after: Clip[] } | null

const cdn = (url: string) =>
  (url || '').replace(
    process.env.NEXT_PUBLIC_URL_1 ?? '',
    process.env.NEXT_PUBLIC_URL_2 ?? '',
  )

const PILL =
  'inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-black/60 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-black/80 disabled:cursor-not-allowed disabled:opacity-50'

export default function ClipChainCard({
  clips,
  t,
  user,
  videoIsLoading,
  onExtend,
  onRedo,
  onDelete,
  onDownload,
  onPublish,
  pictures,
  onAnimate,
}: Props) {
  const scenesOn = useFlag('story_new_scene')
  const [picking, setPicking] = useState(false)
  // The story's width / height, from its video: a new scene's picture must be the same shape to join cleanly.
  const [ratio, setRatio] = useState<number | null>(null)
  const [current, setCurrent] = useState(clips.length - 1)
  const [playingAll, setPlayingAll] = useState(false)
  const [ask, setAsk] = useState<Ask>(null)
  const [joining, setJoining] = useState(false)
  const [error, setError] = useState('')
  const joined = useRef<{ key: string; item: Clip } | null>(null)
  const player = useRef<HTMLVideoElement>(null)

  // A new clip (Continue, or a Try again's result) shows at once; a discard never leaves the index past the end.
  const last = clips.length - 1
  useEffect(() => setCurrent(last), [last, clips[last]?.id])

  const clip = clips[Math.min(current, last)]
  const next = playingAll ? clips[current + 1] : undefined
  const key = useMemo(() => clips.map((c) => c.docRef).join(','), [clips])

  // One <video> for the whole story, never remounted: iOS lets an element play with sound only after a tap started
  // it, and keeps that for the element, so Play all's later clips must reuse the one the tap started.
  useEffect(() => {
    if (playingAll) player.current?.play?.().catch(() => {})
  }, [current, playingAll])

  const playAll = () => {
    const el = player.current
    if (el) {
      el.src = cdn(clips[0].url) // set here, inside the tap, so the play below counts as the person's
      el.muted = false
      el.play?.().catch(() => {})
    }
    setCurrent(0)
    setPlayingAll(true)
  }

  const ended = () => {
    if (playingAll && current < last) setCurrent(current + 1)
    else setPlayingAll(false)
  }

  // The joined video for this exact list of clips, made once and reused by Download and Publish.
  const join = async (): Promise<Clip | null> => {
    if (joined.current?.key === key) return joined.current.item
    if (!user || clips.some((c) => !c.docRef)) return null
    setJoining(true)
    setError('')
    try {
      const res = await joinClips(
        await getIdToken(user),
        clips.map((c) => c.docRef as string),
      )
      const body = await res.json().catch(() => ({}))
      if (!res.ok || !body.doc_ref) {
        setError(body.error || t('clips_join_failed'))
        return null
      }
      const item: Clip = {
        id: body.doc_ref,
        type: 'video',
        url: body.video,
        docRef: body.doc_ref,
        isUrl: true,
      }
      joined.current = { key, item }
      return item
    } catch {
      setError(t('clips_join_failed'))
      return null
    } finally {
      setJoining(false)
    }
  }

  // The picture a scene was animated from: kept on the scene when hosted, else found in the feed by id.
  const sceneSource = (c: Clip): Picture | null => {
    const source = c.sceneOf?.source
    if (!source) return null
    if (source.url) return { ...source, url: source.url }
    return pictures.find((p) => p.id === source.id) ?? null
  }

  // A scene: a picture animated by the feed's Animate, or (no picture) the words `prompt` turned into a video, landing
  // after `parentId` (in place of `replacesId`) in the story's shape.
  const animateScene = (
    p: Picture | null,
    parentId?: string,
    replacesId?: string,
    prompt?: string,
  ) =>
    onAnimate({
      ...p,
      scene: {
        sceneOf: p
          ? {
              parentId,
              source: {
                id: p.id,
                docRef: p.docRef,
                isUrl: p.isUrl,
                url: p.isUrl ? p.url : undefined,
              },
            }
          : { parentId, prompt },
        replacesId,
        orientation: ratio ? ORIENTATION[shapeOf(ratio)] : undefined,
      },
    })

  // New scene: the choice of describing it or starting from a picture, straight to describing when there's no picture.
  const newScene = () =>
    pictures.length ? setPicking(true) : animateScene(null, clips[last].docRef)

  const confirm = () => {
    if (!ask) return
    const { kind, clip: target, after } = ask
    setAsk(null)
    // The story goes on from it: the parts after it go first, so the story never shows a part without what precedes.
    for (const c of [...after].reverse()) onDelete(c)
    if (kind === 'discard') onDelete(target)
    else if (isScene(target)) {
      const { parentId, prompt } = target.sceneOf ?? {}
      animateScene(sceneSource(target), parentId, target.id, prompt)
    } else onRedo(target)
  }

  // Try again only for a part still unsaved, as the feed's own Try again (a kept one stays as it is): a clip extends
  // its video again, a scene is made again from its picture or its words.
  const canTryAgain =
    (isClip(clip) ||
      (isScene(clip) && (!!sceneSource(clip) || !!clip.sceneOf?.prompt))) &&
    !clip.savedStatus &&
    (clip.url || '').includes('temp')

  return (
    <div className="relative mx-auto flex w-fit max-w-[min(680px,100%)] flex-col items-center gap-2 lg:max-w-[min(880px,100%)]">
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-3 z-10 rounded-full border border-white/20 bg-black/60 px-2.5 py-1 text-[0.65rem] font-semibold text-white">
          {t('clips_story', { n: clips.length })}
        </span>
        <video
          ref={player}
          src={cdn(clip.url)}
          className="block h-auto w-auto max-w-full rounded-2xl object-contain"
          style={{ maxHeight: 'calc(var(--feed-media-max, 70dvh) - 150px)' }}
          autoPlay
          loop={!playingAll}
          muted
          playsInline
          controls
          onEnded={ended}
          onLoadedMetadata={(e) => {
            const v = e.currentTarget
            if (v.videoHeight) setRatio(v.videoWidth / v.videoHeight)
          }}
        />
        {next && (
          // The next clip, fetched while this one plays, so Play all doesn't stall at the cut.
          <video src={cdn(next.url)} preload="auto" muted className="hidden" />
        )}
      </div>

      <div
        className="flex max-w-full gap-1.5 overflow-x-auto px-1"
        role="tablist"
      >
        {clips.map((c, i) => (
          <button
            key={c.id}
            type="button"
            role="tab"
            aria-selected={i === current}
            onClick={() => {
              setPlayingAll(false)
              setCurrent(i)
            }}
            className={`h-9 min-w-[2.25rem] shrink-0 rounded-lg border px-2 text-xs font-semibold text-white ${
              i === current
                ? 'border-[var(--main-bg-color)] bg-[var(--main-bg-color)]'
                : 'border-white/20 bg-[var(--foreground-bg-color)]'
            }`}
          >
            {isScene(c) && (
              // A scene starts with a cut: marked, so the strip reads as the story will play.
              <FaFilm
                className="mr-1 inline text-[0.6rem]"
                aria-label={t('clips_scene_badge')}
              />
            )}
            {i + 1}
          </button>
        ))}
      </div>

      {/* What comes next: always in view, however many clips the strip scrolls through */}
      <div className="flex items-center justify-center gap-1.5">
        <button
          type="button"
          disabled={videoIsLoading || !clips[last].docRef}
          title={t('clips_continue')}
          data-track="generate.clips_continue"
          onClick={() => onExtend(clips[last])}
          className="h-9 shrink-0 rounded-lg border border-dashed border-white/40 px-3 text-xs font-semibold text-white disabled:opacity-50"
        >
          <FaPlus className="inline text-[0.7rem]" /> {t('clips_continue')}
        </button>
        {scenesOn && (
          <button
            type="button"
            disabled={videoIsLoading || !clips[last].docRef}
            title={t('clips_new_scene')}
            data-track="generate.clips_new_scene"
            onClick={newScene}
            className="h-9 shrink-0 rounded-lg border border-dashed border-white/40 px-3 text-xs font-semibold text-white disabled:opacity-50"
          >
            {/* A plus, as Continue: both add to this story, neither makes a video of its own */}
            <FaPlus className="inline text-[0.7rem]" /> {t('clips_new_scene')}
          </button>
        )}
      </div>
      {scenesOn && (
        <p className="-mt-1 text-center text-[0.7rem] text-gray-400">
          {t('clips_next_hint')}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-center gap-1.5">
        <button
          type="button"
          className={PILL}
          data-track="generate.clips_play_all"
          onClick={playAll}
        >
          <FaPlay className="text-[0.7rem]" />
          {t('clips_play_all')}
        </button>
        {canTryAgain && (
          <button
            type="button"
            className={PILL}
            disabled={videoIsLoading}
            data-track="generate.clips_try_again"
            onClick={() =>
              setAsk({ kind: 'again', clip, after: clipsAfter(clips, clip) })
            }
          >
            <FaRedo className="text-[0.7rem]" />
            {t('tryAgainExtend')}
          </button>
        )}
        <button
          type="button"
          className={PILL}
          data-track="generate.clips_discard"
          onClick={() =>
            setAsk({ kind: 'discard', clip, after: clipsAfter(clips, clip) })
          }
        >
          <FaTrash className="text-[0.7rem]" />
          {t('clips_discard')}
        </button>
        <button
          type="button"
          className={PILL}
          disabled={joining}
          data-track="generate.clips_download"
          onClick={async () => {
            const item = await join()
            if (item) onDownload(item)
          }}
        >
          <FaDownload className="text-[0.7rem]" />
          {joining ? t('clips_joining') : t('clips_download')}
        </button>
        <button
          type="button"
          className={PILL}
          disabled={joining}
          data-track="generate.clips_publish"
          onClick={async () => {
            const item = await join()
            if (item) onPublish(item)
          }}
        >
          <FaGlobeAmericas className="text-[0.7rem]" />
          {t('clips_publish')}
        </button>
      </div>
      {error && (
        <p role="alert" className="text-center text-xs text-amber-300">
          {error}
        </p>
      )}

      <ScenePicker
        isOpen={picking}
        onClose={() => setPicking(false)}
        pictures={pictures}
        storyRatio={ratio}
        t={t}
        onDescribe={() => {
          setPicking(false)
          animateScene(null, clips[last].docRef)
        }}
        onPick={(p) => {
          setPicking(false)
          animateScene(p, clips[last].docRef)
        }}
      />
      <Modal
        isOpen={!!ask}
        closeModal={() => setAsk(null)}
        title={
          ask?.kind === 'again'
            ? t('tryAgainExtend')
            : t('clips_discard_title', {
                n: ask?.clip ? clips.indexOf(ask.clip) + 1 : 1,
              })
        }
        content={
          ask && ask.after.length > 0
            ? t('clips_after_body', { count: ask.after.length })
            : ask?.kind === 'again'
              ? t('tryAgainExtendTitle')
              : t('clips_discard_body')
        }
        confirmation={
          ask?.kind === 'again' ? t('tryAgainExtend') : t('clips_discard')
        }
        onConfirm={confirm}
      />
    </div>
  )
}
